import React, { useEffect, useState,useContext, useMemo } from "react";
import { Typography, Divider, IconButton } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import SearchInput from "../../components/SearchInput";
import { OutlineButton } from "../../components/Button";
import { PopoverWrapper } from "./styles";
import CollectionBookmarkGrid from "../../components/CollectionBookmarkGrid";
import { useMattoState } from "../../../MattoState";
import { CollectionInterface, CollectionUserImages, CollectionUserMaterials } from "ts-interfaces";
import { gtagViewItemEvent } from "../../../libs/util";
import useEventListener from "../../../libs/useEventListener";
import { useLoginState } from "../../auth/LoginState";
import { ActionButton } from "../../../buttons/ButtonStyles";
import { SearchBox } from "../../../components/SearchBox";
import { useEnsureProjects } from "../../../hooks/useEnsureProjects";
import { usePermissions, firebaseHelper } from 'ui';
import { useProjectSchemas } from "../../../global-state/useProjectSchemas";
import { COLLECTION_TYPES, useCollections } from "../../../global-state/useCollections";
import { toggleProductInCollection } from "../../../v2/apis/collectionsSchemeApi";

export const CollectionPopover = ({
  product, userImage, userMaterial, currentUserUid, popoverRef, popoverPosition, toggleCollection,showNewCollectionModal,onClose,
  ...rest
}) => {

  const [searchValue, setSearchValue] = useState("");
  // We'll use the collections service directly instead of LoginState
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  
  // Get collections from the unified hook using selectors
  const {
    collections,
    initialUserCollectionsFetchDone,
    isLoadingUserCollections,
    fetchCollections,
    getTeamsProAccess,
    getOwnedCollections,
    getSharedCollections,
    getEditableCollections,
    getProjectCollections,
    updateCollection
  } = useCollections(state => ({
    collections: state.collections,
    initialUserCollectionsFetchDone: state.initialUserCollectionsFetchDone,
    isLoadingUserCollections: state.isLoadingUserCollections,
    fetchCollections: state.fetchCollections,
    getTeamsProAccess: state.getTeamsProAccess,
    getOwnedCollections: state.getOwnedCollections,
    getSharedCollections: state.getSharedCollections,
    getEditableCollections: state.getEditableCollections,
    getProjectCollections: state.getProjectCollections,
    updateCollection: state.updateCollection
  }));
  
  // Get projects data
  const { projects, ownedProjects, sharedProjects, permissions: projectPermissions } = useEnsureProjects();
  
  // Filter shared projects to only include those where user has write access
  const writeableSharedProjects = useMemo(() => {
    if (!projectPermissions?.projectWrite) return [];
    return sharedProjects.filter(project => projectPermissions.projectWrite.includes(project.id));
  }, [sharedProjects, projectPermissions?.projectWrite]);
  
  
  // Get permissions and project schemas
  const { permissions, hasCollectionAccess, hasProjectAccess } = usePermissions(currentUserUid, firebaseHelper.db);
  const { getProjectOwner } = useProjectSchemas(state => ({ getProjectOwner: state.getProjectOwner }));
  
  // State for section expansion
  const [expandedSections, setExpandedSections] = useState({
    [COLLECTION_TYPES.OWNED_PROJECT]: true,
    [COLLECTION_TYPES.SHARED_PROJECT]: true,
    [COLLECTION_TYPES.INDIVIDUAL]: true,
    [COLLECTION_TYPES.SHARED]: true
  });

  // Compute derived collections using the getter functions
  const derivedCollections = useMemo(() => {
    // Get all collections using the getter functions
    const ownedCollections = getOwnedCollections();
    const sharedCollections = getSharedCollections();
    const projectCollections = getProjectCollections();
    const editableCollections = getEditableCollections();
    
    // Separate individual vs project collections
    const myIndividualCollections = ownedCollections.filter(c => c.collectionType === COLLECTION_TYPES.INDIVIDUAL);
    const sharedIndividualCollections = sharedCollections.filter(c => c.collectionType === COLLECTION_TYPES.SHARED);
    const myProjectCollections = projectCollections.filter(c => c.uid === currentUserUid);
    const sharedProjectCollections = collections.filter(c => 
      c.collectionType === COLLECTION_TYPES.SHARED_PROJECT 
    );
    
    return {
      myIndividualCollections,
      sharedIndividualCollections,
      myProjectCollections,
      sharedProjectCollections,
      editableCollections
    };
  }, [collections, getOwnedCollections, getSharedCollections, getProjectCollections, getEditableCollections, currentUserUid]);

  const calculatePopupSize = () => {
    if (popoverRef.current) {
      const width = popoverRef.current.offsetWidth;
      const height = popoverRef.current.offsetHeight;
      calculatePopupPosition(width, height);
    }
  }
  
  useEffect(() => { calculatePopupSize(); }, [])
  
  useEffect(() => { 
    // Initialize collections when component mounts
    if (currentUserUid && !initialUserCollectionsFetchDone) {
      fetchCollections(currentUserUid, permissions);
    }
  }, [currentUserUid, permissions, initialUserCollectionsFetchDone, fetchCollections]);

  // Collections are now handled entirely by useCollections
  
  useEventListener('keydown', (event) => {  if (event.key === 'Escape') onClose(false) })

  const collectionToggle = async (collectionId, product, userImage, userMaterial) => {
    if (product) gtagViewItemEvent(product)
    if (window?.gtag) window.gtag('event', 'toggle_collection')
    const selectedCollection: CollectionInterface | undefined = derivedCollections.editableCollections.find(collection => collection.id === collectionId);
    
    if (!selectedCollection) {
      console.error('Collection not found');
      return;
    }
    
    // Use the collections service to toggle the product
    try {
      const updatedCollection = await toggleProductInCollection(
        selectedCollection, 
        product?.id || '', 
        product?.src || '', 
        userImage as CollectionUserImages, 
        userMaterial as CollectionUserMaterials
      );
      
      // Update the unified collections store
      updateCollection(updatedCollection);
      
      useMattoState.getState().setSnackbarMessage(`${product?.name || userImage?.name || userMaterial?.name} updated in ${selectedCollection?.name || 'collection'}`);
      onClose?.();
    } catch (error) {
      console.error('Error toggling product in collection:', error);
      useMattoState.getState().setSnackbarMessage('Error updating collection');
    }
  } 

  const calculatePopupPosition = (popupWidth, popupHeight) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let adjustedX = popoverPosition.x;
    let adjustedY = popoverPosition.y;

    // Check if the popup overflows on the right side of the screen
    if (popoverPosition.x + popupWidth > viewportWidth) {
        adjustedX = popoverPosition.x - popupWidth;
        if(adjustedX < 0) adjustedX = 0;
    }
    // Check if the popup overflows on the bottom of the screen
    if (popoverPosition.y + popupHeight > viewportHeight) {
        adjustedY = popoverPosition.y - popupHeight;
        if(adjustedY < 0) adjustedY = 0;
    }
    setPopupPosition({ x: adjustedX, y: adjustedY });
  };

  useEffect(() => {
    if(popupPosition.x > 0 && popoverRef.current?.style) {
      popoverRef.current.style.visibility = 'visible';
    }
  }, [popupPosition]);

  const createNewCollection = () => {
    onClose(false);
    useMattoState.getState().setModal({name:'collection',id:null, product:product, userImage:userImage, userMaterial:userMaterial});    
  }

  const handleClickOutside = (event) => {
    if (popoverRef?.current && !popoverRef.current.contains(event.target)) {
      onClose(false);
    }
  };
  useEventListener('mousedown', handleClickOutside);
  const handleSearch = (val) => void setSearchValue(val);
  
  // Toggle section expansion
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Helper function to get project name by ID
  const getProjectName = (projectId: string): string => {
    const allProjects = [...ownedProjects, ...writeableSharedProjects];
    const project = allProjects.find(p => p.id === projectId);
    return project ? project.name : 'Loading...';
  };

  // Helper function to group collections by project
  const groupCollectionsByProject = (collections: CollectionInterface[]) => {
    const grouped: { [projectId: string]: CollectionInterface[] } = {};
    const ungrouped: CollectionInterface[] = [];

    collections.forEach(collection => {
      if (collection.associatedProjectId) {
        if (!grouped[collection.associatedProjectId]) {
          grouped[collection.associatedProjectId] = [];
        }
        grouped[collection.associatedProjectId].push(collection);
      } else {
        ungrouped.push(collection);
      }
    });

    return { grouped, ungrouped };
  };

  // Apply search filter and editable filter to collections from the hook
  // IMPORTANT: Only show collections that exist in LoginState.collections (can be saved)
  const filteredCollections = useMemo(() => {
    const editableCollectionIds = new Set(derivedCollections.editableCollections.map(c => c.id));
    
    const applyFilters = (collections: CollectionInterface[]) => {
      // First filter to only editable collections
      let filtered = collections.filter(collection => editableCollectionIds.has(collection.id));
      
      // Then apply search filter if there's a search term
      if (searchValue?.length) {
        filtered = filtered.filter(collection => 
          collection.name.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
      
      return filtered;
    };

    console.log('filteredCollections', derivedCollections.sharedProjectCollections);

    return {
      myProjectCollections: applyFilters(derivedCollections.myProjectCollections),
      sharedProjectCollections: applyFilters(derivedCollections.sharedProjectCollections),
      myIndividualCollections: applyFilters(derivedCollections.myIndividualCollections),
      sharedIndividualCollections: applyFilters(derivedCollections.sharedIndividualCollections)
    };
  }, [searchValue, derivedCollections]);

  const renderCollectionSection = (
    title: string, 
    collections: CollectionInterface[], 
    sectionKey: string,
    showDivider: boolean = true,
    isIndividualCollection: boolean = false // Add this parameter to identify individual collections
  ) => {
    if (collections.length === 0) return null;

    const isExpanded = expandedSections[sectionKey];

    return (
      <>
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '8px 9px 4px 9px',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onClick={() => toggleSection(sectionKey)}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <Typography 
            style={{
              fontFamily: "Roundo-Regular", 
              fontSize: "13px", 
              fontWeight: '600',
              color: 'black',
              letterSpacing: '0.5px'
            }}
          >
            {title} ({collections.length})
          </Typography>
          <IconButton 
            size="small" 
            style={{ padding: '2px', color: '#666' }}
          >
            {isExpanded ? <Remove fontSize="small" /> : <Add fontSize="small" />}
          </IconButton>
        </div>
        
        {isExpanded && (
          <>
            {isIndividualCollection ? (
              // For individual collections, render directly without project grouping
              collections.map((collection: CollectionInterface, index) => (
                <CollectionBookmarkGrid 
                  key={`individual-${index}`} 
                  product={product} 
                  userImage={userImage} 
                  userMaterial={userMaterial} 
                  collection={collection} 
                  toggleCollection={collectionToggle}  
                />
              ))
            ) : (
              // For project collections, apply project grouping logic
              (() => {
                const { grouped, ungrouped } = groupCollectionsByProject(collections);
                return (
                  <>
                    {/* Render grouped collections by project */}
                    {Object.entries(grouped).map(([projectId, projectCollections]) => (
                      <div key={projectId}>
                        <Typography 
                          style={{
                            fontFamily: "Roundo-Regular", 
                            fontSize: "13px", 
                            fontWeight: '500',
                            color: 'black',
                            marginLeft: '9px',
                            marginTop: '4px',
                            marginBottom: '2px',
                          }}
                        >
                          {getProjectName(projectId)} ({projectCollections.length})
                        </Typography>
                        {projectCollections.map((collection: CollectionInterface, index) => (
                          <CollectionBookmarkGrid 
                            key={`${projectId}-${index}`} 
                            product={product} 
                            userImage={userImage} 
                            userMaterial={userMaterial} 
                            collection={collection} 
                            toggleCollection={collectionToggle}  
                          />
                        ))}
                      </div>
                    ))}
                    
                    {/* Render ungrouped collections */}
                    {ungrouped.map((collection: CollectionInterface, index) => (
                      <CollectionBookmarkGrid 
                        key={`ungrouped-${index}`} 
                        product={product} 
                        userImage={userImage} 
                        userMaterial={userMaterial} 
                        collection={collection} 
                        toggleCollection={collectionToggle}  
                      />
                    ))}
                  </>
                );
              })()
            )}
          </>
        )}
        
        {showDivider && <Divider style={{ margin: '8px 0' }} />}
      </>
    );
  };

  if (!useLoginState.getState().profile) return null;
  
  // Use the filtered collections from the hook
  const { myProjectCollections: filteredMyProjectCollections, sharedProjectCollections: filteredSharedProjectCollections, myIndividualCollections: filteredMyIndividualCollections, sharedIndividualCollections: filteredSharedIndividualCollections } = filteredCollections;
  
  const hasAnyCollections = filteredMyProjectCollections.length > 0 || filteredSharedProjectCollections.length > 0 || 
                           filteredMyIndividualCollections.length > 0 || filteredSharedIndividualCollections.length > 0;

  const { isProUser, isTeamsProUser } = getTeamsProAccess();

  return (
    <>
      <PopoverWrapper data-testid="collection-popover" sx={{top:popupPosition.y, left:popupPosition.x, position: 'fixed', visibility: 'hidden' }} ref={popoverRef} {...rest} id='collectionPopover'>
          <Typography style={{margin:'6px 9px', fontFamily: "Roundo-Regular", fontSize: "15px", fontWeight:'800'}} className="title">Add To Collection</Typography>
            <SearchBox onChange={handleSearch} placeholder="Search..." onBlur={() => {}}  />
          <div className="collection-list">
            {hasAnyCollections ? (
              <>
                {!isProUser && !isTeamsProUser ? (
                  // For free users, show all collections without section headers
                  [...filteredMyProjectCollections, ...filteredMyIndividualCollections].map((collection: CollectionInterface, index) => (
                    <CollectionBookmarkGrid 
                      key={`free-user-${index}`} 
                      product={product} 
                      userImage={userImage} 
                      userMaterial={userMaterial} 
                      collection={collection} 
                      toggleCollection={collectionToggle}  
                    />
                  ))
                ) : (
                  // For PRO users, show sectioned collections
                  <>
                    {/* In My Projects Section - Show for Regular PRO and Teams PRO users */}
                    {(isProUser === true || isTeamsProUser === true) && renderCollectionSection(
                      "In My Projects", 
                      filteredMyProjectCollections, 
                      COLLECTION_TYPES.OWNED_PROJECT,
                      (isTeamsProUser === true && filteredSharedProjectCollections.length > 0) || filteredMyIndividualCollections.length > 0 || (isTeamsProUser === true && filteredSharedIndividualCollections.length > 0),
                      false // Project collections
                    )}
                    {/* In Shared Projects Section - Show only for Teams PRO users */}
                    {isTeamsProUser === true && renderCollectionSection(
                      "In Shared Projects", 
                      filteredSharedProjectCollections, 
                      COLLECTION_TYPES.SHARED_PROJECT,
                      filteredMyIndividualCollections.length > 0 || filteredSharedIndividualCollections.length > 0,
                      false // Project collections
                    )}
                    {/* My Individual Collections Section - Show for all users */}
                    {renderCollectionSection(
                      "My Individual Collections", 
                      filteredMyIndividualCollections, 
                      COLLECTION_TYPES.INDIVIDUAL,
                      isTeamsProUser === true && filteredSharedIndividualCollections.length > 0,
                      true // Individual collections
                    )}
                    {/* Shared Individual Collections Section - Show only for Teams PRO users */}
                    {isTeamsProUser === true && renderCollectionSection(
                      "Shared Individual Collections", 
                      filteredSharedIndividualCollections, 
                      COLLECTION_TYPES.SHARED,
                      false,
                      true // Individual collections
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="no-collections">
                {(isLoadingUserCollections || !initialUserCollectionsFetchDone) ? (
                  <Typography>Loading Collections...</Typography>
                ) : collections.length === 0 ? (
                  // User has no collections at all - show encouraging message
                  <div style={{ textAlign: 'center', padding: '20px 10px' }}>
                    <Typography 
                      style={{
                        fontFamily: "Roundo-Regular", 
                        fontSize: "14px", 
                        fontWeight: '600',
                        color: '#333',
                        marginBottom: '8px'
                      }}
                    >
                      You haven't saved any collections yet
                    </Typography>
                    <Typography 
                      style={{
                        fontFamily: "Roundo-Regular", 
                        fontSize: "13px", 
                        color: '#3d3d3d',
                        lineHeight: '1.4'
                      }}
                    >
                      Create your first collection to organize and save your favorite materials, products, and images in one place
                    </Typography>
                  </div>
                ) : (
                  // User has collections but search filtered them out
                  <Typography>No Collections Found</Typography>
                )}
              </div>
            )}
          </div>
          <ActionButton variant="contained" onClick={createNewCollection}>
            Create New Collection
          </ActionButton>
      </PopoverWrapper>
      </>
  );
};
export default CollectionPopover;
